import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Download, Trash2, Loader2 } from "lucide-react";
import { api, errMsg, useFileUrl } from "@/lib/api";
import { setOrbState } from "@/lib/orb";
import { AuroraOrb } from "@/components/AuroraOrb";

const ImageCard = ({ item: i, onDelete }) => {
  const src = useFileUrl(i.id);
  return (
    <figure className="hud-card overflow-hidden group" data-testid={`image-item-${i.id}`}>
      {src ? <img src={src} alt={i.prompt} className="w-full aspect-square object-cover" /> : <div className="w-full aspect-square bg-white/5 animate-pulse" />}
      <figcaption className="p-3 flex items-start gap-2">
        <p className="text-xs text-slate-400 line-clamp-2 flex-1">{i.prompt}</p>
        <a href={src || undefined} download={`aurora-${i.id}.png`} className="text-cyan-300 hover:text-amber-300" data-testid={`image-download-${i.id}`}><Download size={15} /></a>
        <button onClick={() => onDelete(i.id)} className="text-slate-500 hover:text-rose-400" data-testid={`image-delete-${i.id}`}><Trash2 size={15} /></button>
      </figcaption>
    </figure>
  );
};

export const ImagesView = ({ active }) => {
  const [prompt, setPrompt] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/images").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const gen = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setBusy(true);
    setOrbState("elaborazione");
    try {
      const { data } = await api.post("/images", { prompt });
      setItems((x) => [data, ...x]);
      setPrompt("");
      toast.success("Immagine creata");
    } catch (err) { toast.error(errMsg(err, "Generazione non riuscita")); }
    setOrbState("idle");
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/images/${id}`); setItems((x) => x.filter((i) => i.id !== id)); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6" data-testid="images-view">
      <p className="hud-tag mb-2">Nano Banana · Gemini</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">Studio Immagini</h2>
      <form onSubmit={gen} className="hud-card p-4 sm:p-5 flex flex-col sm:flex-row gap-3 items-stretch">
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} disabled={!active || busy} data-testid="image-prompt-input"
          placeholder={active ? "Descrivi l'immagine: es. 'Una città futuristica sotto l'aurora boreale, stile cinematografico'" : "Abbonamento scaduto"}
          className="flex-1 bg-[#050b18]/80 border border-cyan-500/25 rounded-xl p-3 text-sm outline-none focus:border-cyan-400/60 resize-none" />
        <button disabled={!active || busy || !prompt.trim()} data-testid="image-generate-btn" className="btn-aurora rounded-xl px-6 py-3 text-sm uppercase flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} Genera
        </button>
      </form>
      {busy && <div className="flex flex-col items-center py-8"><AuroraOrb size={150} testId="image-orb" /><p className="hud-tag !text-violet-300">Rendering in corso…</p></div>}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6" data-testid="image-gallery">
        {items.map((i) => <ImageCard key={i.id} item={i} onDelete={del} />)}
      </div>
      {!busy && items.length === 0 && <p className="text-sm text-slate-500 mt-8 text-center">La tua galleria è vuota. Crea la prima immagine!</p>}
    </div>
  );
};
